// @levelup/crm-roles
//
// Single source of truth for CRM in-app role definitions.
//
// Central owns role BEHAVIOR (capabilities, scopes, permissions).
// This package owns role MEANING + UI metadata (labels, descriptions,
// colors, ordering, Central mapping).
//
// Web, Mobile, and Desktop all consume this package.
// Do NOT duplicate these definitions in any consumer.
// ============================================================
// Central member-preset template IDs
// ============================================================
/**
 * Template IDs registered in Central for CRM member presets.
 * Central uses these to assign the correct scope set on invite.
 */
export const MEMBER_PRESET_TEMPLATE_IDS = {
    agent: '00000000-0000-0000-0000-000000000201',
    coordinator: '00000000-0000-0000-0000-000000000202',
    analyst: '00000000-0000-0000-0000-000000000203',
};
// ============================================================
// Role definitions
// ============================================================
export const CRM_ROLES = {
    owner: {
        key: 'owner',
        centralRole: 'owner',
        label: 'Owner',
        shortDescription: 'Full workspace control including billing',
        badgeColor: 'purple',
        order: 0,
        invitable: false,
    },
    administrator: {
        key: 'administrator',
        centralRole: 'admin',
        label: 'Administrator',
        shortDescription: 'Manage team, settings, and integrations. Can delete records.',
        badgeColor: 'blue',
        order: 1,
        invitable: true,
    },
    agent: {
        key: 'agent',
        centralRole: 'member',
        templateId: MEMBER_PRESET_TEMPLATE_IDS.agent,
        label: 'Agent',
        shortDescription: 'Create and manage contacts, deals, tasks. Make calls and send messages.',
        badgeColor: 'green',
        order: 2,
        invitable: true,
        isDefault: true,
    },
    coordinator: {
        key: 'coordinator',
        centralRole: 'member',
        templateId: MEMBER_PRESET_TEMPLATE_IDS.coordinator,
        label: 'Coordinator',
        shortDescription: 'Everything Agents can do, plus automations, forms, analytics, and AI configuration.',
        badgeColor: 'indigo',
        order: 3,
        invitable: true,
    },
    analyst: {
        key: 'analyst',
        centralRole: 'member',
        templateId: MEMBER_PRESET_TEMPLATE_IDS.analyst,
        label: 'Analyst',
        shortDescription: 'Read-only access to all CRM data, pipeline, and reports.',
        badgeColor: 'slate',
        order: 4,
        invitable: true,
    },
    viewer: {
        key: 'viewer',
        centralRole: 'viewer',
        label: 'Viewer',
        shortDescription: 'Read-only access to assigned data only.',
        badgeColor: 'gray',
        order: 5,
        invitable: true,
    },
};
// ============================================================
// Derived lists (pre-sorted, pre-filtered)
// ============================================================
/** All CRM roles sorted by display order. */
export const CRM_ROLE_LIST = Object.values(CRM_ROLES).sort((a, b) => a.order - b.order);
/** Roles available in the invite dropdown, sorted by display order. */
export const INVITABLE_ROLES = CRM_ROLE_LIST.filter((r) => r.invitable);
/** The default role for new invites. */
export const DEFAULT_INVITE_ROLE = INVITABLE_ROLES.find((r) => r.isDefault) ?? INVITABLE_ROLES[0];
// ============================================================
// Mapping helpers
// ============================================================
/**
 * Resolve a CRM in-app role from a Central role + optional template ID.
 *
 * Used when displaying a team member's role: Central provides `role` and
 * optionally `templateId`; this function returns the specific CRM role.
 *
 * For `member` without a recognized templateId, defaults to `agent`.
 */
export function getCrmRoleForCentralRole(centralRole, templateId) {
    // Direct-mapped roles (no variants)
    if (centralRole === 'owner')
        return CRM_ROLES.owner;
    if (centralRole === 'admin')
        return CRM_ROLES.administrator;
    if (centralRole === 'viewer')
        return CRM_ROLES.viewer;
    // Member variants — resolve by templateId
    if (centralRole === 'member') {
        if (templateId) {
            const match = CRM_ROLE_LIST.find((r) => r.centralRole === 'member' && r.templateId === templateId);
            if (match)
                return match;
        }
        // Default member variant is Agent
        return CRM_ROLES.agent;
    }
    // Unknown Central role — defensive fallback
    return CRM_ROLES.viewer;
}
/**
 * Convert a CRM in-app role key to the Central values needed for invite/API calls.
 *
 * Returns the Central role and optional templateId to send to Central.
 */
export function getCentralRoleForCrmRole(crmRoleKey) {
    const config = CRM_ROLES[crmRoleKey];
    return {
        role: config.centralRole,
        templateId: config.templateId,
    };
}
/**
 * Look up a CRM role config by its key. Returns undefined for invalid keys.
 */
export function getCrmRoleConfig(key) {
    return CRM_ROLES[key];
}
// ============================================================
// Badge style helpers (platform-agnostic tokens)
// ============================================================
/**
 * Tailwind CSS classes for badge rendering (Web).
 * Consumers can use this directly or map badgeColor to their own system.
 */
export const BADGE_STYLES = {
    purple: { text: 'text-purple-700', bg: 'bg-purple-100' },
    blue: { text: 'text-blue-700', bg: 'bg-blue-100' },
    green: { text: 'text-green-700', bg: 'bg-green-100' },
    indigo: { text: 'text-indigo-700', bg: 'bg-indigo-100' },
    slate: { text: 'text-slate-700', bg: 'bg-slate-100' },
    gray: { text: 'text-gray-500', bg: 'bg-gray-50' },
};
/**
 * Get Tailwind badge classes for a CRM role key.
 * Falls back to gray if role is unknown.
 */
export function getBadgeClasses(roleKeyOrCentralRole, templateId) {
    // Try direct CRM role key first
    const direct = getCrmRoleConfig(roleKeyOrCentralRole);
    if (direct) {
        const style = BADGE_STYLES[direct.badgeColor];
        return { ...style, label: direct.label };
    }
    // Fall back to Central role resolution
    const resolved = getCrmRoleForCentralRole(roleKeyOrCentralRole, templateId);
    const style = BADGE_STYLES[resolved.badgeColor];
    return { ...style, label: resolved.label };
}
//# sourceMappingURL=index.js.map